import type { Ingredient } from './types';
import type { IngredientInput } from './validation';
import { matchIngredient, type IngredientMatch } from './recipeProposal';
import { parseImporte, type ScannedLine } from './receipts';
import { parseUnit } from './importCatalog';
import { isPurchaseUnit, type PurchaseUnit } from './units';

export interface CatalogPriceLine {
  index: number;
  description: string;
  name: string;
  match: IngredientMatch;
  action: 'update' | 'create' | 'choose' | 'ignore';
  unitPrice?: number;
  unit?: PurchaseUnit;
  warning?: string;
}
export interface CatalogPricesProposal { lines: CatalogPriceLine[] }

/** El precio leído es por unidad facturada, no por el formato previo del catálogo. */
export function invoiceLinesToCatalogProposal(lines: ScannedLine[], catalog: Ingredient[]): CatalogPricesProposal {
  return { lines: lines.map((line, index) => {
    const description = line.description?.trim() ?? '';
    const name = description.replace(/\b\d+(?:[.,]\d+)?\s*(kg|g|gr|ml|cl|l|litros?|gramos?|kilos?)\b/gi, '').trim();
    const match = matchIngredient(name, catalog);
    const unitToken = `${line.quantity ?? ''} ${description}`.match(/\b(kg|g|gr|ml|cl|l|litros?|gramos?|kilos?|uds?|pz|docena)\b/i)?.[1];
    const parsedUnit = unitToken ? parseUnit(unitToken) : undefined;
    const unit = isPurchaseUnit(parsedUnit) ? parsedUnit : undefined;
    const price = parseImporte(line.unitPrice);
    const unitPrice = price != null && Number.isFinite(price) && price > 0 ? price : undefined;
    const ignore = !name || /\b(detergente|lej[ií]a|papeler[ií]a|transporte|portes|descuento|iva|subtotal|total)\b/i.test(name);
    return { index, description, name, match,
      action: ignore ? 'ignore' : match.kind === 'ambiguous' ? 'choose' : match.kind === 'none' ? 'create' : 'update',
      unitPrice, unit,
      warning: !unit ? 'Indica la unidad y cantidad a las que corresponde el precio de esta factura.' : !unitPrice ? 'Falta un precio unitario válido sin IVA.' : undefined,
    };
  }) };
}

/** Se llama solo tras revisar precio y formato, conservando el resto de la ficha. */
export function catalogPriceInput(current: Ingredient | undefined, name: string, price: number, qty: number, unit: PurchaseUnit): IngredientInput {
  return { ...current, name, group: current?.group ?? 'otros', vatPct: current?.vatPct ?? 0,
    purchaseQty: qty, purchaseUnit: unit, netPrice: price, pricePending: false,
    priceSource: 'factura', createdVia: current?.createdVia ?? 'factura', active: current?.active ?? true,
  };
}
